import React from "react";
import { FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck, FormInput } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	InvitationApi,
	OrganizationUserApi,
} from "../../../../../../../../services/Api/Organization/organizationTypes";
import { OrganizationApplicationApi } from "../../../../../../../../services/Api/types";

interface Props {
	fields: FieldArrayWithId<OrganizationUserApi, "applications", "key">[];
	remove: UseFieldArrayRemove;
	append: UseFieldArrayAppend<OrganizationUserApi | InvitationApi, "applications">;
	form: UseFormReturn<OrganizationUserApi> | UseFormReturn<InvitationApi>;
	applications: OrganizationApplicationApi[];
}

const OrganizationApplicationsTableForm = ({ applications, remove, append, fields, form }: Props) => {
	const { t } = useTranslation();
	const sortedApplications = applications.sort((a, b) => a.name.localeCompare(b.name));
	const {
		formState: { errors },
		register,
		control,
	} = form;

	const getIndex = (applicationType: string) => {
		const fieldsTypes = fields.map((field) => field.application_type);
		let index = fields.findIndex((x) => x.application_type === applicationType);
		if (index !== -1) {
			return index;
		}

		const diffApps = sortedApplications.filter((app) => !fieldsTypes.includes(app.slug));
		index = diffApps.findIndex((x) => x.slug === applicationType);
		if (index === -1) {
			return index;
		}

		index = index + fieldsTypes.length;
		return index;
	};

	const changeApplication = (application: OrganizationApplicationApi, index: number) => {
		const applicationType = application.slug;

		if (fields.some((field) => field.application_type === applicationType)) {
			remove(index);
		} else {
			append({
				application_type: applicationType,
			});
		}
	};

	const getRoles = (slug: string) => {
		const application = applications.filter((app) => app.slug === slug)[0];
		if (application === undefined || application.roles === undefined) return [];

		return application.roles.map((role) => {
			return {
				label: role.name,
				value: role.uid,
			};
		});
	};

	return (
		<table className="table table-form">
			<thead>
				<tr>
					<th>{t("modules.user.field.app.title")}</th>
					<th className="w-75">{t("modules.user.field.role.title")}</th>
				</tr>
			</thead>
			<tbody>
				{sortedApplications.map((application) => {
					let enabled: boolean;
					let field: FieldArrayWithId<OrganizationUserApi, "applications", "key"> | undefined;
					const newIndex = fields.findIndex((f) => f.application_type === application.slug);
					if (newIndex !== -1) {
						enabled = true;
						field = fields[newIndex];
					} else {
						enabled = false;
					}
					const indexPut = getIndex(application.slug);

					return (
						<tr key={application.slug}>
							<td style={{ textAlignLast: "left" }}>
								{enabled && (
									<FormInput
										errors={errors}
										type="hidden"
										register={register}
										name={`applications.${indexPut}.application_type`}
										value={application.slug}
									/>
								)}
								<FormCheck
									type="switch"
									checked={enabled}
									errors={errors}
									name={`applications.${indexPut}.enabled`}
									register={null}
									onChange={() => changeApplication(application, indexPut)}
									label={application.name}
								/>
							</td>
							<td>
								{enabled ? (
									<FormSelectGroup
										name={`applications.${indexPut}.role_id`}
										errors={errors}
										control={control}
										options={getRoles(application.slug)}
										data-testid={`applications.${indexPut}.role_id`}
									/>
								) : (
									<FormInput
										name={`applications.${indexPut}.role_id`}
										errors={errors}
										register={null}
										disabled
										defaultValue={field ? field.role_id : undefined}
									/>
								)}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
};

export default OrganizationApplicationsTableForm;
